import React from 'react';
import { Card, Typography, Tooltip, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Text } = Typography;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 40px);
`;

const StyledCard = styled(Card)`
  height: 100%;

  .ant-card-body {
    padding: 24px;
    height: 100%;
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    .ant-typography {
      margin: 0;
      color: #1f3eb8;
      font-weight: 600;
    }

    .info-icon {
      color: #8c8c8c;
      cursor: pointer;
    }
  }

  .issues-container {
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: calc(100% - 40px);
  }

  .issue-item {
    text-align: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition:
      background-color 0.2s,
      transform 0.1s;

    &:hover {
      background-color: #f5f5f5;
      transform: translateY(-2px);

      .issue-label {
        text-decoration: underline;
        text-decoration-color: #9b9b9b;
        text-decoration-thickness: 1px;
        text-underline-offset: 3px;
      }
    }

    .issue-count {
      font-size: 2rem;
      font-weight: bold;
      line-height: 1;
      margin-bottom: 4px;
    }

    .issue-label {
      font-size: 1rem;
      text-transform: capitalize;
    }

    &.critical {
      .issue-count,
      .issue-label {
        color: #d4380d;
      }
    }

    &.major {
      .issue-count,
      .issue-label {
        color: #ff812a;
      }
    }

    &.minor {
      .issue-count,
      .issue-label {
        color: #d6e019;
      }
    }
  }
`;

const AssetsWithIssuesWidget = ({
  isLoading = false,
  issuesData = { critical: 0, major: 0, minor: 0 },
  onIssueClick,
}) => {
  const tooltipContent = (
    <div>
      <div>
        • Displays how many assets have at least one Critical, Major, or Minor
        issue.
      </div>
      <br />
      <div>
        • E.g., if an asset has two critical issues and one major issue, it will
        count once under "Critical" and once under "Major."
      </div>
    </div>
  );

  const handleIssueClick = (priority) => {
    if (onIssueClick) {
      onIssueClick(priority);
    }
  };

  return (
    <StyledCard>
      <div className="widget-header">
        <Text strong>Assets with Issues</Text>
        <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: '420px' }}>
          <ExclamationCircleOutlined className="info-icon" />
        </Tooltip>
      </div>
      {isLoading ? (
        <LoadingContainer>
          <Spin />
        </LoadingContainer>
      ) : (
        <div className="issues-container">
          <div
            className="issue-item critical"
            onClick={() => handleIssueClick('critical')}
          >
            <div className="issue-count">{issuesData.critical}</div>
            <div className="issue-label">Critical</div>
          </div>

          <div
            className="issue-item major"
            onClick={() => handleIssueClick('major')}
          >
            <div className="issue-count">{issuesData.major}</div>
            <div className="issue-label">Major</div>
          </div>

          <div
            className="issue-item minor"
            onClick={() => handleIssueClick('minor')}
          >
            <div className="issue-count">{issuesData.minor}</div>
            <div className="issue-label">Minor</div>
          </div>
        </div>
      )}
    </StyledCard>
  );
};

export default AssetsWithIssuesWidget;
